import { InfoIcon, Loader2Icon } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { INVITE_RECIPIENT_MODAL_LABELS } from '@/modals/invite-recipient-modal/labels'

import { Alert, AlertIcon } from '@/components/lemmala-design-system/alert/alert'
import { Button } from '@/components/lemmala-design-system/button/button'
import { InputEmails } from '@/components/lemmala-design-system/input-emails/input-emails'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { UserBadge } from '@/components/shared/user-badge/user-badge'

type InviteRecipientModalProps = {
  onAddRecipient: (recipientEmails: string[]) => Promise<void>
  onRemoveRecipient?: (recipientId: string) => Promise<void>
  recipientEmails: string[]
  hideExisting?: boolean
  suggestRecipientsGroups?: { groupName: string; groupMembers: { email: string; name: string }[] }[]
  isLoading?: boolean
  businessOwnerCc?: { name: string; email: string; thirdPartyName: string }
}

export const useInviteRecipientModal = ({
  onAddRecipient,
  onRemoveRecipient,
  recipientEmails,
  hideExisting = false,
  suggestRecipientsGroups,
  isLoading = false,
  businessOwnerCc,
}: InviteRecipientModalProps) => {
  const [open, setOpen] = useState(false)
  const [recipients, setRecipients] = useState<string[]>([])
  const [removedRecipients, setRemovedRecipients] = useState<string[]>([])
  const modalRef = useRef<HTMLDivElement>(null)

  const reset = useCallback(() => {
    setRecipients([])
    setRemovedRecipients([])
    return true
  }, [])

  const invite = useCallback(async () => {
    if (onRemoveRecipient && removedRecipients.length > 0) {
      await Promise.all(removedRecipients.map((email) => onRemoveRecipient(email)))
    }

    if (recipients.length > 0) {
      await onAddRecipient(recipients)
    }

    reset()
    setOpen(false)
  }, [onRemoveRecipient, removedRecipients, recipients, onAddRecipient, reset])

  const renderModal = useCallback(() => {
    const visibleRecipients = recipientEmails.filter((email) => !removedRecipients.includes(email))
    const hasExistingRecipients = visibleRecipients.length > 0 && !hideExisting
    const hasChanges = recipients.length > 0 || removedRecipients.length > 0

    return (
      <Modal
        title={INVITE_RECIPIENT_MODAL_LABELS.modalTitle}
        size={hasExistingRecipients ? 'medium' : 'small'}
        open={open}
        footer={
          <div className='mt-6 flex w-full flex-col gap-4'>
            {recipients.length > 0 && (
              <Alert
                icon={<AlertIcon icon={InfoIcon} />}
                variant='info'
                description={INVITE_RECIPIENT_MODAL_LABELS.newRecipientsInvite}
              />
            )}
            <div className='flex justify-end gap-2'>
              <Button variant={'ghost'} onClick={() => reset() && setOpen(false)}>
                {INVITE_RECIPIENT_MODAL_LABELS.buttons.cancel}
              </Button>
              <Button onClick={() => invite()} disabled={!hasChanges || isLoading}>
                {isLoading && <Loader2Icon className='size-4 animate-spin' />}
                {INVITE_RECIPIENT_MODAL_LABELS.buttons.save}
              </Button>
            </div>
          </div>
        }
        onCancel={() => reset() && setOpen(false)}
        subtitle={INVITE_RECIPIENT_MODAL_LABELS.body}
      >
        <div
          ref={modalRef}
          className={`flex min-h-32 gap-4 ${hasExistingRecipients ? 'flex-row' : 'flex-col'}`}
        >
          <div className={`flex flex-col gap-4 ${hasExistingRecipients ? 'w-1/2' : 'w-full'}`}>
            <div className='flex flex-col gap-1'>
              <div className='text-label-default'>
                {INVITE_RECIPIENT_MODAL_LABELS.addRecipients}
              </div>
              <InputEmails
                container={modalRef}
                recipients={recipients}
                onRecipientChange={setRecipients}
                suggestRecipientsGroups={suggestRecipientsGroups}
                excludeEmails={visibleRecipients}
              />
            </div>
          </div>

          {hasExistingRecipients && (
            <div className={'w-1/2 flex-col gap-4'}>
              <div className='flex flex-col gap-1'>
                <div className='text-label-default'>
                  {INVITE_RECIPIENT_MODAL_LABELS.existingRecipients}
                </div>
                <div className='flex flex-wrap gap-2'>
                  {visibleRecipients.map((email) => (
                    <UserBadge
                      className='rounded-full'
                      key={email}
                      name={email}
                      tooltipDisabled
                      onRemove={
                        onRemoveRecipient &&
                        (() => {
                          setRemovedRecipients((prev) => [...prev, email])
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        {businessOwnerCc && (
          <div className='mt-4 flex flex-col gap-2.5 border-t border-main-border-default pt-3'>
            <div className='flex flex-col'>
              <div className='text-label-default'>
                {INVITE_RECIPIENT_MODAL_LABELS.businessOwnerCc}
              </div>
              <div className='text-main-foreground-secondary text-body-default'>
                {INVITE_RECIPIENT_MODAL_LABELS.businessOwnerCcDescription(
                  businessOwnerCc.thirdPartyName,
                )}
              </div>
            </div>
            <UserBadge
              className='rounded-full'
              name={businessOwnerCc.name}
              email={businessOwnerCc.email}
              tooltipDisabled
            />
          </div>
        )}
      </Modal>
    )
  }, [
    recipientEmails,
    hideExisting,
    recipients,
    removedRecipients,
    open,
    isLoading,
    suggestRecipientsGroups,
    businessOwnerCc,
    invite,
    onRemoveRecipient,
    reset,
  ])

  return { renderModal, openModal: () => setOpen(true) }
}
